/**
 * №14.1.1 — the universal drill-down Back affordance.
 *
 * A single shell-level control (rendered once in `AppShellLayout`, above the
 * routed `<Outlet>`), so EVERY drill-down gets a Back button with zero per-page
 * code. It shows only when the current route is NOT a top-level nav destination
 * — the rail's own targets (Matters, Trust, Firm settings, …) never get one;
 * anything reached by drilling in (a matter, a contact, a ledger, an account)
 * does. The top-level set is derived from the same sitemap the rail renders, so
 * the two can never disagree.
 *
 * "Back" = browser history when there is an in-app entry to return to (so it
 * lands exactly where the user came from — the Parties tab, say); on a cold
 * deep-link (no in-app history) it falls back to the nearest top-level ancestor
 * of the current path.
 */
import { useLocation, useNavigate } from "react-router";

/** Nearest top-level ancestor of `path` — walk segments off the end until one
 *  is a known nav destination; else the app home. */
function parentOf(path: string, topLevel: Set<string>): string {
  const parts = path.split("/").filter(Boolean); // ["app","place","contact","C-1"]
  for (let i = parts.length - 1; i > 1; i--) {
    const candidate = `/${parts.slice(0, i).join("/")}`;
    if (topLevel.has(candidate)) return candidate;
  }
  return "/app";
}

export function BackBar({ topLevel }: { topLevel: Set<string> }) {
  const location = useLocation();
  const navigate = useNavigate();

  const path = location.pathname.replace(/\/+$/, "") || "/app";
  // Top-level nav destination → no Back (David: "top level nav clicks don't need this").
  if (topLevel.has(path)) return null;

  // React Router keeps a monotonic history index in `history.state.idx`; > 0
  // means there is a prior in-app entry we can return to.
  const idx = (typeof window !== "undefined"
    ? (window.history.state as { idx?: number } | null)?.idx
    : undefined) ?? 0;

  const onBack = () => {
    if (idx > 0) navigate(-1);
    else navigate(parentOf(path, topLevel));
  };

  return (
    <div className="backbar">
      <button type="button" className="backbtn" onClick={onBack}>
        <svg className="g" viewBox="0 0 16 16" fill="none" aria-hidden="true">
          <path d="M10 3.5 5.5 8l4.5 4.5" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
        Back
      </button>
    </div>
  );
}
